import SectionPanel from '@/components/SectionPanel';
import { useOrganization } from '@/auth/AuthContext';
import { BiWindow } from 'react-icons/bi';
import {
    Text, Box, Stack,
    SimpleGrid, HStack, Textarea, useDisclosure
} from "@chakra-ui/react"
import CurrencySelectController from '@/components/fields/CurrencySelect';
import ErrorMessage from "src/components/ErrorMessage";
import { Controller, useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState, JSX } from 'react';
import { projectService } from 'src/service/ProjectService';
import { partnerService } from 'src/service/PartnerService';
import { useNavigate } from 'react-router-dom';
import DataTable from 'src/components/DataTable';
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext } from 'src/components/DataTable';
import { ProjectSummary } from 'src/api/ProjectSummary';
import CountrySelectController from '@/components/fields/CountrySelect';
import DateSelectController from '@/components/fields/DateSelect';
import { YearlyBudget } from 'src/api/YearlyBudget';
import { formatOverview } from 'src/data/DateTimeUtil';
import BudgetDialog from './BudgetDialog';
import { AmountUtil } from 'src/data/AmountUtil';
import ConfirmDialog from 'src/components/ConfirmDialog';
import {
    OptionBase,
    Select as ReactSelect
} from "chakra-react-select";
import AddButton from 'src/components/buttons/AddButton';
import ColorPicker from 'src/components/ColorPicker';
import CancelButton from 'src/components/buttons/CancelButton';
import SubmitButton from '@/components/buttons/SubmitButton';
import { FormInput } from '@/components/fields/FormInput';
import { Field } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { Radio, RadioGroup } from "@/components/ui/radio"
import { DropDownMenu } from '@/components/DropDownMenu';
import ExplanationPopover from '@/components/ExplanationPopover';
import { toaster } from '@/components/ui/toaster';
import FormPage from '@/components/FormPage';
import StatCard from '@/components/StatCard';
import { useI18n } from '@/i18n/I18nContext';


export interface ProjectsFormProps {
    projectId: number;
    organizationId: number;
    onSubmit: (ps: ProjectSummary) => Promise<void>;
    viewOnly?: boolean;
    header: string;
}

export interface PartnerOption extends OptionBase {
    label: string;
    value: number;
}

const LoadedProjectsForm = ({ projectId, organizationId, onSubmit, viewOnly, header }: ProjectsFormProps): JSX.Element => {
    const { t } = useI18n();
    const organization = useOrganization()!;
    const homeCurrency = organization.currency;
    const { control, register, handleSubmit, reset, getValues, formState: { errors, isSubmitting }, watch } = useForm<ProjectSummary>({
        defaultValues: {
            project: {
                id: projectId,
                name: '',
                currency: homeCurrency,
                startDate: new Date(),
                endDate: new Date(),
                internal: false,
                color: projectId > -1 ? '#FFFFFF' : '#FE9200', // otherwise flickers
                createdAt: new Date(),
                organizationId: organizationId,
            },
            budget: [],
            partnerIds: []
        }
    });
    const [serverError, setServerError] = useState('');

    // fixed or open
    const [budgetType, setBudgetType] = useState<string>('fixed');
    // use a default, will be updated before the modal opens
    const [changeBudget, setChangeBudget] = useState<YearlyBudget>(
        {
            projectId: -1,
            year: new Date().getFullYear(),
            budget: undefined,
            projectBudget: undefined,
            rate: undefined,
            rateDate: new Date(),
            indicativeRate: false,
            fundraisingBudget: undefined,
            overhead: 20,
            organizationId: organizationId
        });
    const { open: isBudgetOpen, onOpen: onBudgetOpen, onClose: onBudgetClose } = useDisclosure();
    const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    // manage the budget list in a state (not in the form)
    const [yearlyBudget, setYearlyBudget] = useState<YearlyBudget[]>([]);
    // manage configured partner ids in a state (not in the form)
    const [partnerIds, setPartnerIds] = useState<number[]>([]);

    const navigate = useNavigate();

    // read all partner details to map it with the partner ids configured to the project
    const [partners, setPartners] = useState([]);
    useEffect(() => {
        partnerService.listAll().then(ps => setPartners(ps.concat()));
    }, []);

    // in edit or view mode fill in the form details, budget and partner ids
    useEffect(() => {
        if (projectId > -1) {
            projectService.find(projectId).then(p => {
                console.info(`found project ${JSON.stringify(p)}`);
                setBudgetType(p.project.endDate ? 'fixed' : 'open');
                setYearlyBudget(p.budget.concat());
                setPartnerIds(p.partnerIds.concat());
                reset(p);
            });
        }
    }, [projectId, reset]);


    const onBudgetChange = useCallback((yb: YearlyBudget): Promise<void> => {
        console.info(`upserting budget for ${yb.year}`);
        setChangeBudget(yb);
        // update data, override the same year
        setYearlyBudget(prev => {
            let old = prev.filter(p => p.year !== yb.year);
            let newBudget: YearlyBudget[] = [].concat(old, yb).sort((a, b) => a.year - b.year);
            return newBudget;
        });
        return Promise.resolve();
    }, []);

    const readOnly = viewOnly ?? false;
    //console.info(`form is readonly: ${readOnly}`);

    const onSubmitInternal = (p: ProjectSummary): Promise<void> => {
        let validated: ProjectSummary = {
            ...p,
            ...{
                project: { ...p.project, ...{ endDate: (budgetType === 'open' ? null : p.project.endDate) } },
                budget: yearlyBudget,
                partnerIds: partnerIds
            }
        }
        console.info(`submitting ${JSON.stringify(validated)}`);
        return onSubmit(validated)
            .then(() => {
                toaster.create({
                    title: p.project.id && p.project.id > -1 ? 'Projekt aktualisiert' : 'Projekt hinzugefügt',
                    type: 'success'
                });
                navigate('/projects');
            })
            .catch(_err => setServerError('Fehler'));
    };


    const amountHeader = (name: string) => () => (<div style={{ fontSize: 'xs', textAlign: 'right', width: '8rem', paddingRight: '.2rem' }}>{name}</div>)
    const amountCell = (fontWeight: string = 'normal') => (info: CellContext<YearlyBudget, number>) =>
        (<Text fontSize='xs' fontWeight={fontWeight} width='8rem' textAlign='right'>{AmountUtil.format(info.getValue())}</Text>)
    const columns: ColumnDef<YearlyBudget>[] = useMemo(
        () => [
            {
                id: 'year',
                header: 'Jahr',
                accessorKey: 'year',
                cell: (info: CellContext<YearlyBudget, number>) => (<Text textStyle='xs' fontWeight='bold'>{info.getValue()}</Text>)
            },
            {
                id: 'budget',
                header: amountHeader('Budget'),
                accessorKey: 'budget',
                cell: amountCell()
            },
            {
                id: 'rate',
                header: () => (<div style={{ textAlign: "right" }}>Kurs</div>),
                accessorFn: (row: YearlyBudget) => AmountUtil.format(row.rate),
                cell: (info: CellContext<YearlyBudget, string>) => (<Text fontSize='xs' textAlign='right'>{info.getValue()}</Text>)
            },
            {
                id: 'rateDate',
                header: () => (<div style={{ textAlign: "right" }}>Kursdatum</div>),
                accessorFn: (row: YearlyBudget) => formatOverview(row.rateDate),
                cell: (info: CellContext<YearlyBudget, string>) => (<Text fontSize='xs' textAlign='right'>{info.getValue()}</Text>)
            },
            {
                id: 'projectChf',
                header: amountHeader(`Projekt ${homeCurrency}`),
                accessorKey: 'projectBudget',
                cell: amountCell('bold')
            },
            {
                id: 'overhead',
                header: () => (<div style={{ textAlign: "right" }}>Overhead</div>),
                accessorKey: 'overhead',
                cell: (info: CellContext<YearlyBudget, number>) => (<Text fontSize='xs' textAlign='right' fontWeight='bold'>{AmountUtil.format(info.getValue(), 1)}%</Text>)
            },
            {
                id: 'fundraisingChf',
                header: amountHeader(`Fundraising ${homeCurrency}`),
                accessorKey: 'fundraisingBudget',
                cell: amountCell('bold')
            },
            {
                id: 'edit',
                enableSorting: false,
                cell: (info) => {
                    let row = info.row.original;
                    return (
                        <>
                            {!readOnly && (
                                <DropDownMenu onMenuEdit={() => {
                                    //console.info(`set to ${JSON.stringify(row)}`);
                                    setChangeBudget(row);
                                    onBudgetOpen();
                                }} onMenuDelete={() => {
                                    //console.info(`set to ${JSON.stringify(row)}`);
                                    setChangeBudget(row);
                                    onDeleteOpen();
                                }} />
                            )}
                        </>
                    )
                }
            }
        ], [onBudgetOpen, onDeleteOpen, readOnly, homeCurrency]
    );

    return (
        <FormPage title={header} description={t('projects.form.description')} maxW='5xl'
            onSubmit={handleSubmit(onSubmitInternal)} invalid={!!serverError}
            actions={<>
                <CancelButton onClick={() => navigate('/projects/')} />
                {!readOnly && <SubmitButton isSubmitting={isSubmitting} />}
            </>}>
            {serverError && <ErrorMessage message={serverError} />}

            <SectionPanel title={t('projects.sections.information')}>
                <SimpleGrid columns={{ base: 1, lg: 2 }} gap='6' minW='0'>
                    <Stack gap={4} minW='0'>
                        <FormInput name="project.name" label='Name' register={register} error={errors.project?.name} required disabled={readOnly} icon={BiWindow} autoFocus={true} fontWeight='bold' />

                        {/* multi select for partners */}
                        <Box minW='0'>
                            <Field id="partnerId" label='Partner Organisationen' disabled={readOnly}>
                                <ReactSelect
                                    isSearchable={true}
                                    isMulti={true}
                                    isClearable={false}
                                    isDisabled={readOnly}
                                    name='partnersId'
                                    inputId='partnerId'
                                    placeholder='Partners'
                                    options={partners.map(p => {
                                        let po: PartnerOption = {
                                            value: p.id,
                                            label: p.name
                                        }
                                        return po;
                                    })}
                                    value={partners
                                        .filter(p => partnerIds.includes(p.id))
                                        .map(p => {
                                            let po: PartnerOption = {
                                                value: p.id,
                                                label: p.name
                                            }
                                            return po;
                                        })}
                                    onChange={val => setPartnerIds(val.map(v => v.value))}
                                />
                            </Field>
                        </Box>

                        <CountrySelectController control={control} name='project.country' readOnly={readOnly} />

                        <CurrencySelectController control={control} name='project.currency' readOnly={readOnly} />
                    </Stack>

                    {/* second column on large screen */}
                    <Stack gap='4' minW='0'>
                        <Field id="budgetType" label='Budget' fontWeight={'bold'} required disabled={readOnly}>
                            <RadioGroup value={budgetType} onValueChange={e => setBudgetType(e.value)} disabled={readOnly}>
                                <Stack direction={{ base: 'column', sm: 'row' }}>
                                    <Radio value='fixed'>Fester Zeitraum</Radio>
                                    <Radio value='open'>Laufend</Radio>
                                </Stack>
                            </RadioGroup>
                        </Field>

                        <Stack direction={{ base: 'column', md: 'row' }} gap='4'>
                            <DateSelectController control={control} required disabled={readOnly} label='Anfangsdatum' name='project.startDate' clearable={false} />
                            <DateSelectController control={control}
                                required={budgetType === 'fixed'}
                                disabled={readOnly || budgetType === 'open'} label='Enddatum' name='project.endDate' clearable={true} />
                        </Stack>

                        <Stack direction={{ base: 'column', md: 'row' }} gap='4' flexWrap='wrap'>
                            <Field id="internal" readOnly={readOnly} disabled={readOnly}>
                                <Controller
                                    control={control}
                                    name='project.internal'
                                    render={({ field: { onChange, value, ref } }) => (
                                        <HStack>
                                            <Switch checked={value} onCheckedChange={e => onChange(e.checked)} ref={ref}>Interne Reserve</Switch>
                                            <ExplanationPopover explanation='Interne Reserven werden von der Fundraising-Übersicht und dem Reporting ausgeschlossen.' />
                                        </HStack>
                                    )}
                                />
                            </Field>

                            <Field id='color' disabled={readOnly}>
                                <Controller
                                    control={control}
                                    name='project.color'
                                    render={({ field: { value, onChange } }) => (
                                        <HStack alignItems="start">
                                            <ColorPicker value={value} onChange={onChange} isReadOnly={readOnly} />
                                            <Text textAlign='start' color={readOnly ? 'gray' : ''}>Projekt Farbe</Text>
                                        </HStack>
                                    )}
                                />
                            </Field>
                        </Stack>

                        <Field id="comments" label='Bemerkungen' readOnly={readOnly}>
                            <Textarea {...register('project.comments')} height='7rem' />
                        </Field>

                    </Stack>
                </SimpleGrid>
            </SectionPanel>

            <SectionPanel title={t('projects.sections.yearlyBudgets')}
                actions={!readOnly && <AddButton caption={t('projects.form.addBudget')} onClick={() => {
                    setChangeBudget({
                        projectId, organizationId, year: new Date().getFullYear(),
                        budget: undefined, projectBudget: undefined, fundraisingBudget: undefined,
                        rate: getValues('project.currency') === homeCurrency ? 1 : undefined,
                        rateDate: new Date(), indicativeRate: false, overhead: 20,
                    });
                    onBudgetOpen();
                }} />}>
                <BudgetDialog
                    data={changeBudget}
                    currency={getValues('project.currency') ?? homeCurrency}
                    isOpen={isBudgetOpen}
                    onClose={onBudgetClose}
                    onConfirm={async (yb: YearlyBudget): Promise<void> => onBudgetChange(yb)}
                />

                <DataTable
                    columns={columns}
                    data={yearlyBudget}
                    initialSortByColumn='year'
                    initialSortByDesc={false}
                    onRowClick={readOnly ? undefined : row => {
                        setChangeBudget(row);
                        onBudgetOpen();
                    }}
                />
                {yearlyBudget.length <= 0 && <Text textAlign='center'>kein Budget eingetragen</Text>}
                <ConfirmDialog
                    heading={`${changeBudget?.year} Löschen`}
                    isOpen={isDeleteOpen}
                    onClose={onDeleteClose}
                    onConfirm={async (): Promise<void> => {
                        setYearlyBudget(prev => {
                            let toDelete = changeBudget
                            let newBudget = prev.filter(p => p.year !== toDelete.year).sort((a, b) => a.year - b.year);
                            return newBudget;
                        });
                        return Promise.resolve();
                    }}
                />


                <SimpleGrid columns={{ base: 1, md: 3 }} gap='4'>
                    <StatCard label={t('projects.sections.totalBudget')}
                        value={AmountUtil.format(yearlyBudget.reduce((a, yb) => a + (yb.budget ?? 0), 0))} unit={watch('project.currency') ?? homeCurrency}
                        valueFontSize='xl' showAccentBorder={false} />
                    <StatCard label={t('projects.sections.projectBudget')}
                        value={AmountUtil.format(yearlyBudget.reduce((a, yb) => a + (yb.projectBudget ?? 0), 0))} unit={homeCurrency}
                        valueFontSize='xl' showAccentBorder={false} />
                    <StatCard label={t('projects.sections.fundraisingBudget')}
                        value={AmountUtil.format(yearlyBudget.reduce((a, yb) => a + (yb.fundraisingBudget ?? 0), 0))} unit={homeCurrency}
                        valueFontSize='xl' showAccentBorder={false} />
                </SimpleGrid>
            </SectionPanel>
        </FormPage>
    );
}

const ProjectsForm = (props: ProjectsFormProps) => {
    const organization = useOrganization();
    if (!organization || organization.id !== props.organizationId) {
        return <Text role='status'>Organisationseinstellungen werden geladen...</Text>;
    }
    return <LoadedProjectsForm key={`${organization.id}:${props.projectId}`} {...props} />;
};

export default ProjectsForm
